import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CourseCard from '../../components/CourseCard/CourseCard';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import { useAuth } from '../../context/AuthContext';
import { getMergedEnrollments } from '../../utils/enrollmentStore';
import { PLACEHOLDER_COURSE_IMAGE } from '../../constants';
import './MyLearning.css';

export default function MyLearning() {
  const { user } = useAuth();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const paymentMessageSent = location.state?.paymentMessageSent === true;

  useEffect(() => {
    if (!user) {
      setCourses([]);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    getMergedEnrollments(user.uid)
      .then((list) => setCourses(list || []))
      .catch(() => setError('Could not load your courses. Please refresh the page.'))
      .finally(() => setLoading(false));
  }, [user]);

  const enrolled = courses;
  const inProgress = enrolled.filter((c) => (c.progress ?? 0) > 0 && (c.progress ?? 0) < 100);
  const completed = enrolled.filter((c) => (c.progress ?? 0) >= 100);

  return (
    <div className="my-learning">
      <div className="my-learning-inner">
        <h1 className="my-learning-title">My Learning</h1>

        {paymentMessageSent && (
          <p className="my-learning-success" role="status">
            Your message has been sent. The course owner will confirm your payment and activate your access. The course will appear here once activated.
          </p>
        )}
        {error && <p className="my-learning-error" role="alert">{error}</p>}

        {loading && enrolled.length === 0 ? (
          <p className="my-learning-loading">Loading your courses…</p>
        ) : (
          <>
            {inProgress.length > 0 && (
              <section className="my-learning-section">
                <h2 className="my-learning-section-title">Continue learning</h2>
                <div className="my-learning-continue">
                  {inProgress.map((course) => (
                    <div key={course.id} className="my-learning-continue-card">
                      <Link to={`/course/${course.id}`} className="my-learning-continue-link">
                        <img src={course.thumbnail || PLACEHOLDER_COURSE_IMAGE} alt="" className="my-learning-continue-img" onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_COURSE_IMAGE; }} />
                        <div className="my-learning-continue-body">
                          <h3 className="my-learning-continue-title">{course.title}</h3>
                          <p className="my-learning-continue-meta">{course.instructor} · {course.level}</p>
                          <ProgressBar value={course.progress ?? 0} height={6} showLabel />
                        </div>
                      </Link>
                      <Link to={`/course/${course.id}`} className="my-learning-resume-btn">Resume</Link>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="my-learning-section">
              <h2 className="my-learning-section-title">
                {inProgress.length > 0 ? 'All my courses' : 'My courses'}
              </h2>
              {enrolled.length === 0 ? (
                <div className="my-learning-empty">
                  <p>You haven’t enrolled in any courses yet.</p>
                  <Link to="/" className="my-learning-empty-link">Browse courses</Link>
                </div>
              ) : (
                <div className="my-learning-grid">
                  {enrolled.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </div>
              )}
            </section>

            {completed.length > 0 && (
              <section className="my-learning-section">
                <h2 className="my-learning-section-title">Completed</h2>
                <div className="my-learning-grid">
                  {completed.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
